import { PocComponentHeader } from 'commons/components/components-wrapper/poc-component-header.component';
import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { ConfirmationModalComponent, SelectOption } from 'commons/components';
import { CustomField } from 'core/service/custom-fields/custom-field.type';
import { Trans } from 'core/i18n';
import { useTranslation } from 'core/i18n/i18n';
import { POCFieldAttribute } from 'modules/settings/pods/settings-integrations/service';
import { DataPointMappingHeaderComponent } from 'modules/settings/pods/settings-integrations/components';
import React, { useMemo } from 'react';
import { usePopupManager } from 'react-popup-manager';
import { newIdentityProviderMapping } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/service/active-directory.business';

import { IdentityProviderMapping } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';
import { DataPointMappingComponent } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/components/data-point-mapping/data-point-mapping.component';
import {
    ActiveDirectoryDataPointsFormFieldsWrapper,
    ActiveDirectoryDataPointsFormHeaderContent,
    ActiveDirectoryDataPointsFormMandatoryDescriptionPocTypography,
    ActiveDirectoryDataPointsFormOtherAddPocButton,
    ActiveDirectoryDataPointsFormOtherDescriptionPocTypography,
    ActiveDirectoryDataPointsFormOtherTitlePocTypography,
    ActiveDirectoryDataPointsOtherFormHeaderContent,
    SettingsCardLayoutStyled,
} from './active-directory-data-points-form.style';

export interface ActiveDirectoryDataPointsFormProps {
    pocFields: POCFieldAttribute[];
    customFields: CustomField[];
    identityProviderAttributes: string[];
    identityProviderMapping: IdentityProviderMapping[];
    onChangeField: (field: string, value: string | IdentityProviderMapping[]) => void;
}

export const ActiveDirectoryDataPointsForm: React.FC<ActiveDirectoryDataPointsFormProps> = ({
    pocFields,
    customFields,
    identityProviderAttributes,
    identityProviderMapping,
    onChangeField,
}) => {
    const { t } = useTranslation();
    const popupManager = usePopupManager();

    const mandatoryPocFields = useMemo<string[]>(
        () => pocFields.filter((f) => f.mandatory).map((f) => f.attribute),
        [pocFields]
    );

    const getMandatoryMapping = (field: string): IdentityProviderMapping =>
        identityProviderMapping.find((m) => m.operatorField === field) ??
        newIdentityProviderMapping(Math.max(0, ...identityProviderMapping.map((m) => m.index)) + 1, field);

    const optionalPocFields = useMemo<SelectOption[]>(
        () =>
            pocFields
                .filter((f) => !f.mandatory)
                .map((f) => ({
                    value: f.attribute,
                    text:
                        customFields.find((c) => c.id === f.attribute)?.name ??
                        t(
                            `settings.integrations.active-directory.data-points.mapping.poc.fields.value.${f.attribute}`,
                            f.attribute
                        ),
                })),
        [pocFields, customFields, t]
    );

    const unmappedIdentityProviderAttributesCount = useMemo(
        () => identityProviderAttributes.length - identityProviderMapping.filter((m) => m.identityProviderField).length,
        [identityProviderAttributes, identityProviderMapping]
    );

    const isAddOptionalIdentityProviderMappingDisabled =
        unmappedIdentityProviderAttributesCount <= 0 ||
        (pocFields.length > 0 && pocFields.length - identityProviderMapping.length < 1);

    const identityProviderAttributeOptions = useMemo(
        () => identityProviderAttributes.map((attr) => ({ value: attr, text: attr })),
        [identityProviderAttributes]
    );

    const onAddOptionalIdentityProviderMapping = () => {
        const updateMapping = [
            ...identityProviderMapping,
            newIdentityProviderMapping(Math.max(0, ...identityProviderMapping.map((m) => m.index)) + 1),
        ];
        onChangeField('identityProviderMapping', updateMapping);
    };

    const onRemoveOptionalIdentityProviderMapping = (index: number) => {
        popupManager.open(ConfirmationModalComponent, {
            title: t(
                'settings.integrations.active-directory.data-points.mapping.optional.delete.confirmation-modal.title'
            ),
            children: t(
                'settings.integrations.active-directory.data-points.mapping.optional.delete.confirmation-modal.description'
            ),
            buttonText: t(
                'settings.integrations.active-directory.data-points.mapping.optional.delete.confirmation-modal.button'
            ),
            actionOnClick: () =>
                Promise.resolve(
                    onChangeField(
                        'identityProviderMapping',
                        identityProviderMapping.filter((m) => m.index !== index)
                    )
                ),
        });
    };

    const onChangeIdentityProviderMapping = <K extends keyof IdentityProviderMapping>(
        index: number,
        field: K,
        value: IdentityProviderMapping[K]
    ) => {
        const updateMappings = [...identityProviderMapping];
        const ipm = updateMappings.find((i) => i.index === index)!;
        ipm[field] = Array.isArray(value) ? value[0] : value;
        onChangeField('identityProviderMapping', updateMappings);
    };

    const handleOnChangeIdentityProviderMapping = <K extends keyof IdentityProviderMapping>(
        index: number,
        field: K,
        value: IdentityProviderMapping[K]
    ) => onChangeIdentityProviderMapping(index, field, value);

    return (
        <SettingsCardLayoutStyled interactive rounded>
            <PocComponentHeader slot="header">
                <ActiveDirectoryDataPointsFormHeaderContent>
                    <PocTypography element="h6" variant="title6">
                        {t('settings.integrations.active-directory.data-points.form.mandatory.title')}
                    </PocTypography>
                    <ActiveDirectoryDataPointsFormMandatoryDescriptionPocTypography variant="body1">
                        {t('settings.integrations.active-directory.data-points.form.mandatory.description')}
                    </ActiveDirectoryDataPointsFormMandatoryDescriptionPocTypography>
                </ActiveDirectoryDataPointsFormHeaderContent>
            </PocComponentHeader>

            <ActiveDirectoryDataPointsFormFieldsWrapper>
                <DataPointMappingHeaderComponent
                    pocMappingHeader={t('settings.integrations.active-directory.data-points.form.mandatory.poc.header')}
                    ldapMappingHeader={t(
                        'settings.integrations.active-directory.data-points.form.mandatory.ldap.header'
                    )}
                />
                {mandatoryPocFields.map((field) => {
                    const mapping = getMandatoryMapping(field);
                    return (
                        <DataPointMappingComponent
                            key={`active-directory-data-points-mandatory-${field}`}
                            identityProviderIndex={mapping.index}
                            pocFieldInputType={'input'}
                            pocFieldInputName={field}
                            pocFieldInputValue={t(
                                `settings.integrations.active-directory.data-points.mapping.poc.fields.value.${field}`,
                                field
                            )}
                            pocFieldInputDisabled
                            ldapFieldInputName={field}
                            ldapFieldInputValue={mapping.identityProviderField}
                            ldapFieldInputOptions={identityProviderAttributeOptions}
                            onChangeIdentityProviderMapping={handleOnChangeIdentityProviderMapping}
                        />
                    );
                })}
            </ActiveDirectoryDataPointsFormFieldsWrapper>

            <ActiveDirectoryDataPointsOtherFormHeaderContent>
                <ActiveDirectoryDataPointsFormOtherTitlePocTypography element="h6" variant="body1">
                    {t('settings.integrations.active-directory.data-points.form.other.title')}
                </ActiveDirectoryDataPointsFormOtherTitlePocTypography>
                <ActiveDirectoryDataPointsFormOtherDescriptionPocTypography variant="body1">
                    {
                        <Trans
                            t={t}
                            i18nKey={'settings.integrations.active-directory.data-points.form.other.description'}
                            values={{
                                number: Math.max(0, unmappedIdentityProviderAttributesCount),
                            }}
                        />
                    }
                </ActiveDirectoryDataPointsFormOtherDescriptionPocTypography>
            </ActiveDirectoryDataPointsOtherFormHeaderContent>

            <ActiveDirectoryDataPointsFormFieldsWrapper>
                {identityProviderMapping
                    .filter((m) => !mandatoryPocFields.includes(m.operatorField))
                    .map((m) => (
                        <DataPointMappingComponent
                            key={`active-directory-data-points-optional-${m.index}`}
                            identityProviderIndex={m.index}
                            pocFieldInputType={'select'}
                            pocFieldInputName={m.operatorField}
                            pocFieldInputOptions={optionalPocFields}
                            pocFieldInputValue={m.operatorField}
                            ldapFieldInputName={m.identityProviderField}
                            ldapFieldInputValue={m.identityProviderField}
                            ldapFieldInputOptions={identityProviderAttributeOptions}
                            identityProviderMapping={identityProviderMapping}
                            onChangeIdentityProviderMapping={handleOnChangeIdentityProviderMapping}
                            onRemove={() => onRemoveOptionalIdentityProviderMapping(m.index)}
                        />
                    ))}
            </ActiveDirectoryDataPointsFormFieldsWrapper>

            <ActiveDirectoryDataPointsFormOtherAddPocButton
                variant="tertiary"
                onClick={onAddOptionalIdentityProviderMapping}
                disabled={isAddOptionalIdentityProviderMappingDisabled}
            >
                <PocIcon slot="prefix" name="circle_add" family="outlined"></PocIcon>
                {t('settings.integrations.active-directory.data-points.form.other.button.add')}
            </ActiveDirectoryDataPointsFormOtherAddPocButton>
        </SettingsCardLayoutStyled>
    );
};
